import {
  useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { mapObjectProperties } from '~/_shared/utils/collections/collections';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import {
  type StorageService, useStorageService,
} from '~/_shared/utils/storageService';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { extraBoundaryFillsLoaded } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import { extraFillConfigPerBTGIdSelector } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFillSelectors';

type BoundaryExtraFillsPerBoundaryGroupId = Parameters<typeof extraBoundaryFillsLoaded>[0];
type BoundaryExtraFill = BoundaryExtraFillsPerBoundaryGroupId[number][number];
const BoundaryExtraFillsLocalStorageKey = 'boundaryExtraFills';

const safeParse = (value: string) => {
  try {
    return JSON.parse(value);
  }
  catch {
    return null;
  }
};

const loadBoundaryExtraFillFromLocalStorage = (storageService: StorageService): BoundaryExtraFillsPerBoundaryGroupId => {
  const extraFills = storageService.getLocalStorageItem(BoundaryExtraFillsLocalStorageKey);

  if (!extraFills) {
    return {};
  }

  const parsed = safeParse(extraFills);

  if (!parsed) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(parsed)
      .map(([boundaryGroupIdString, groupExtraFills]) => {
        const boundaryGroupId = parseInt(boundaryGroupIdString, 10);
        if (isNaN(boundaryGroupId) || !Array.isArray(groupExtraFills)) {
          return null;
        }
        const groupExtraFillsValidated = groupExtraFills
          .map(x =>
            // TODO: Add validation. Return null if invalid.
            ({
              ...x,
            }) as BoundaryExtraFill)
          .filter(notNullsy);

        return [boundaryGroupId, groupExtraFillsValidated];
      })
      .filter(notNullsy),
  );
};

export const BoundaryExtraFillLocalStorageSyncContainer = () => {
  const extraFillDataPerBoundaryGroupId = useSelector(extraFillConfigPerBTGIdSelector);
  const [extraFillsLoaded, setExtraFillsLoaded] = useState(false);
  const dispatch = useDispatch();
  const storageService = useStorageService();

  useEffect(() => {
    dispatch(extraBoundaryFillsLoaded(loadBoundaryExtraFillFromLocalStorage(storageService)));
    setExtraFillsLoaded(true);
  }, [dispatch, storageService]);

  useEffect(() => {
    if (extraFillsLoaded) {
      const extraFillsPerBTGId = mapObjectProperties(groupData => groupData.extraFills,
        extraFillDataPerBoundaryGroupId);
      storageService.setLocalStorageItem(BoundaryExtraFillsLocalStorageKey, JSON.stringify(extraFillsPerBTGId));
    }
  }, [extraFillsLoaded, extraFillDataPerBoundaryGroupId, storageService]);

  return null;
};
